import React, {
    createContext,
    useContext,
    useState,
    useMemo,
    PropsWithChildren,
    useEffect,
} from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { NotificationProvider } from './NotificationContext';
import * as Sentry from '@sentry/react';

const ORG_ID_KEY = 'picked-organization-id';

type OperatorContext = {
    organizationId?: string;
    subdomain?: string;
    loading?: boolean;
    setOrganizationId?: (id: string) => void;
    setLoading?: (loading: boolean) => void;
};

type OperatorProps = PropsWithChildren<{
    subdomain: string;
}>;

export const OperatorContext = createContext<OperatorContext>({});

export const OperatorProvider: React.FC<OperatorProps> = ({
    subdomain,
    children,
}) => {
    const [loading, setLoading] = useState(true);
    const [organizationId, setOrganizationId] = useLocalStorage(ORG_ID_KEY, '');

    useEffect(() => {
        if (subdomain && organizationId) {
            // REVIEW: why saving id as null on the local storage?
            setOrganizationId(null);

            if (organizationId !== '') {
                Sentry.getGlobalScope().setUser({
                    organizationId,
                    subdomain,
                });
            }
        }
    }, [subdomain]);

    const value = useMemo(
        () => ({
            organizationId,
            setOrganizationId,
            subdomain,
            loading,
            setLoading,
        }),
        [organizationId, subdomain, loading],
    );

    return (
        <OperatorContext.Provider value={value}>
            <NotificationProvider>{children}</NotificationProvider>
        </OperatorContext.Provider>
    );
};

export const useOperator = (): OperatorContext => useContext(OperatorContext);
